import { SupplyShedFilters } from '@cibo/core'
import { UserDataKey, useUserData } from '@cibo/profile'
import {
  GridColumnVisibilityModel,
  GridPaginationModel,
  GridRowSelectionModel,
} from '@mui/x-data-grid-pro'
import {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react'
import { UserSupplyShedManagementContext } from '../UserSupplyShedManagement'

export const USER_SUPPLY_SHED_TABLE_PAGE_SIZES = [10, 25, 50, 100]

export interface UserSupplyShedTableState {
  // these convenience functions translate from table-row spec to resourceIds
  onDelete(): void

  gridRowSelectionModel: GridRowSelectionModel
  setGridRowSelectionModel(model: GridRowSelectionModel): void
  clearSelectedRowIds(): void

  pageSize: number
  setPageSize(pageSize: number): void
  page: number
  setPage(page: number): void
  paginationModel: GridPaginationModel
  setPaginationModel(model: GridPaginationModel): void
  columnVisibility: GridColumnVisibilityModel
  setColumnVisibility: Dispatch<SetStateAction<GridColumnVisibilityModel>>
  filters: SupplyShedFilters
  setFilters: (filters: SupplyShedFilters) => void
}

export const UserSupplyShedTableContext = createContext<UserSupplyShedTableState>({
  filters: {},
  setFilters: console.log,
  gridRowSelectionModel: { type: 'include', ids: new Set() },
  setGridRowSelectionModel: console.log,
  clearSelectedRowIds: console.log,

  onDelete: console.log,

  pageSize: USER_SUPPLY_SHED_TABLE_PAGE_SIZES[0],
  setPageSize: console.log,
  paginationModel: { pageSize: 25, page: 0 },
  setPaginationModel: console.log,
  page: 0,
  setPage: console.log,
  columnVisibility: {},
  setColumnVisibility: console.log,
})
UserSupplyShedTableContext.displayName = 'UserSupplyShedTableContext'

type UserSupplyShedTableProviderProps = {
  persistKey?: UserDataKey
}

export const UserSupplyShedTableProvider = ({
  children,
  persistKey,
}: PropsWithChildren<UserSupplyShedTableProviderProps>) => {
  const { data, update } = useUserData(persistKey)
  const [isReady, setIsReady] = useState(false)

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: USER_SUPPLY_SHED_TABLE_PAGE_SIZES[0],
  })
  const [filters, setFilters] = useState<SupplyShedFilters>({})
  const [columnVisibility, setColumnVisibility] = useState<GridColumnVisibilityModel>({})

  useEffect(() => {
    if (!isReady && data) {
      // @ts-ignore data is not typed
      setPaginationModel(data.pageSize || USER_SUPPLY_SHED_TABLE_PAGE_SIZES[0])
      // @ts-ignore data is not typed
      setFilters(data.filters || {})
      // @ts-ignore data is not typed
      setColumnVisibility(data.columnVisibility || {})
      setIsReady(true)
    }
  }, [data, isReady])

  useEffect(() => {
    if (isReady) {
      update({
        filters,
        columnVisibility,
        pageSize: paginationModel.pageSize,
      })
    }
  }, [filters, columnVisibility, paginationModel, isReady])

  const [gridRowSelectionModel, setGridRowSelectionModel] = useState<GridRowSelectionModel>({
    type: 'include',
    ids: new Set(),
  })
  const clearSelectedRowIds = () => setGridRowSelectionModel({ type: 'include', ids: new Set() })

  const { setDeleteModal } = useContext(UserSupplyShedManagementContext)

  return (
    <UserSupplyShedTableContext.Provider
      value={{
        filters,
        setFilters,

        gridRowSelectionModel,
        setGridRowSelectionModel,
        clearSelectedRowIds,

        paginationModel,
        setPaginationModel,
        pageSize: paginationModel.pageSize,
        setPageSize: (pageSize: number) => setPaginationModel(prev => ({ ...prev, pageSize })),
        page: paginationModel.page,
        setPage: (page: number) => setPaginationModel(prev => ({ ...prev, page })),

        columnVisibility: columnVisibility as GridColumnVisibilityModel,
        setColumnVisibility: setColumnVisibility as Dispatch<
          SetStateAction<GridColumnVisibilityModel>
        >,

        onDelete: () =>
          setDeleteModal({
            open: true,
            selectedIds: Array.from(gridRowSelectionModel.ids.values()) as string[],
            onSuccess: clearSelectedRowIds,
          }),
      }}
    >
      {children}
    </UserSupplyShedTableContext.Provider>
  )
}
